/**
 * OAuth callback screen (/auth/callback)
 *
 * Handles OAuth redirect after IdP authentication (Google, Apple, Microsoft).
 *
 * FLOW:
 * 1. Fetch auth session (uses fresh OAuth tokens from code exchange)
 * 2. Parse ID token claims (user info without API call)
 * 3. Route based on user state:
 *    • Existing user (has custom:user_id) → load user → /directory
 *    • New user (no custom:user_id) → /npi-verification (no DB user yet)
 *
 * NEW USER FLOW (OAuth Verify-Then-Create):
 * - Navigate to /npi-verification → user authenticated but restricted
 * - User provides NPI → POST /v2/auth/complete-signup creates DB user
 * - Backend sets custom:user_id after user creation
 * - Sets Cognito sub as temporary userId until DB user is created
 * - Marks auth state as resolved so user can access NPI verification screen
 *
 * EXISTING USER FLOW:
 * - If verified email matches, backend auto-links (seamless, transparent)
 * - loadCurrentUser() → parse token claims with custom:user_id
 * - Navigate to /directory → fully authenticated
 * - If arriving from manual conflict resolution, attempts account linking automatically
 * - For existing users with linking context, attempts manual account linking before routing
 *
 * ACCOUNT LINKING INTEGRATION:
 * - Auto-link: Backend PreSignUp Lambda links matching verified emails (no frontend action)
 * - Manual link: For NPI/email conflicts, uses saved context to call /v2/auth/link-provider
 * - Linking context loaded once at flow start, used for link_intent and post-Google linking
 * - Context cleared only after successful link or when stale (>15min)
 *
 * RECONCILE FLOW:
 * - POST /v2/auth/reconcile-provider detects if Google identity is already linked (detection-only)
 * - PreSignUp Lambda handles all automatic linking before this call
 * - After linking, verify session via /v2/users/me
 * - On 401, trigger one silent re-auth (prompt=none) to mint unified tokens, then verify again
 * - For linked cases: Response includes linkStatus ('new' | 'linked') as primary field
 *   - linkStatus: 'new' = just linked (fire Account linked event with linkStatus: 'new')
 *   - linkStatus: 'linked' = already linked (no Account linked event, only Log in event)
 * - For non-linked cases: Response includes outcome ('npi_unverified', 'federated_only', etc.)
 * - Analytics: Account linked event fires when linkStatus === 'new' (actual linking occurred)
 *
 * MOBILE SECURITY:
 * - Tokens stored in AsyncStorage with prefix: @MemoryStorage:CognitoIdentityServiceProvider.*
 *
 * GUARDS:
 * - useRef prevents duplicate code exchanges (React Strict Mode)
 * - Cache-Control: no-store prevents browser caching
 * - OAuth state parsed via URLSearchParams for robust link_intent and silent flags
 *
 * UI STATE MANAGEMENT:
 * - OAuth users: defer hasCustomUserId/isNewUser until reconcile completes (token may lack custom:user_id).
 * - Initial: "Signing in…" (primary only).
 * - After reconcile: linked/existing → "Welcome back!" (no secondary), new → "Welcome!" + "Setting up your account…", linking → "Linking your account…" + "This may take a moment."
 * - Secondary text only for new users and linking, never for existing users.
 *
 * Display name policy:
 * - Do not use IdP claim names for UI. Prefer authoritative DB user names when available.
 * - New OAuth users (no DB yet) show a generic welcome (no name) until signup completes.
 *
 * Error UI policy:
 * - Hard-stop errors (linking missing destination, IdP switching, invalid code, generic token
 *   validation failures) render a warning box with Sign in and Contact Support actions.
 * - Intentional aborts (access_denied) show a simple “Back to sign in”.
 */

import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { fetchAuthSession } from 'aws-amplify/auth';
import { randomUUID } from 'expo-crypto';
import { useRouter, useLocalSearchParams, Href } from 'expo-router';
import { useSetAtom } from 'jotai';
import { AlertTriangleIcon } from 'lucide-react-native';
import { useEffect, useMemo, useRef, useState } from 'react';

import { SupportForm } from '@/components/supportForm';
import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Link, LinkText } from '@/components/ui/link';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/hooks/useAuth';
import { buildAuthPathWithRedirect, performRedirectNavigation } from '@/hooks/useRedirectPath';
import { clearAccountLinkingContext, loadAccountLinkingContext } from '@/services/accountLinking';
import {
  buildOAuthUrl,
  getOAuthConfig,
  loadOAuthRedirectPath,
  clearOAuthRedirectPath,
} from '@/services/auth';
import { getCurrentUserV2 } from '@/services/getCurrentUserV2';
import { authStateResolvedAtom, authenticatedUserIdAtom } from '@/services/globalState';
import { linkProvider } from '@/services/linkProvider';
import { reconcileProvider } from '@/services/reconcileProvider';
import { parseIdTokenClaims } from '@/services/session';
import { segmentClient } from '@/util/analytics';
import {
  trackAccountLinked,
  trackAccountLinkingFailed,
  trackAuthCallbackError,
  trackLoginSuccess,
} from '@/util/analyticsEvents';
import { isApiErrorV2 } from '@/util/fetchClient';
import { Sentry } from '@/util/sentry';

const SESSION_STORAGE_KEYS = {
  SILENT_REAUTH_ATTEMPTED: 'oauth_silent_retry_done',
} as const;

type OAuthClientError =
  | 'invalid_code'
  | 'invalid_state'
  | 'access_denied'
  | 'token_validation_failed'
  | 'idp_switching_not_allowed';

/**
 * Triggers silent re-authentication with prompt=none to mint fresh tokens
 * after account linking. Does not specify identity_provider to let Cognito
 * resolve the linked identity automatically.
 */
function triggerSilentReauth() {
  if (!isWeb || typeof window === 'undefined') {
    return;
  }

  const config = getOAuthConfig();

  if (!config) {
    console.error('[Auth] Failed to get OAuth config for silent re-auth');

    return;
  }

  const url = buildOAuthUrl({
    ...config,
    prompt: 'none',
    state: `silent=1&nonce=${randomUUID()}`,
  });

  window.location.replace(url);
}

function useQueryParams() {
  const params = useLocalSearchParams();

  return useMemo(() => {
    const code = (params.code as string) || '';

    const state = (params.state as string) || '';

    const error = (params.error as string) || '';

    const errorDescription = (params.error_description as string) || '';

    const returnTo = (params.returnTo as string) || '';

    return { code, state, error, errorDescription, returnTo };
  }, [params]);
}

export default function OAuthCallbackScreen() {
  const { replace } = useRouter();

  const { loadCurrentUser } = useAuth();

  const setUserId = useSetAtom(authenticatedUserIdAtom);

  const setAuthStateResolved = useSetAtom(authStateResolvedAtom);

  const { code, state, error } = useQueryParams();

  const stateParams = useMemo(() => new URLSearchParams(state || ''), [state]);

  const hasLinkIntent = stateParams.get('link_intent') === '1';

  const defaultRedirectPath = '/(secure)/(tabs)/directory';

  /**
   * Redirects to the appropriate destination after successful OAuth.
   * Loads redirect_path from storage if present, otherwise defaults to directory.
   * Cleans up storage after retrieving the redirect path.
   */
  const redirectAfterAuth = async () => {
    const redirectPath = await loadOAuthRedirectPath();

    await clearOAuthRedirectPath();

    performRedirectNavigation(redirectPath, defaultRedirectPath, replace);
  };

  /*
   * The destination the OAuth hop was carrying, kept so the failure paths below can hand
   * it back. It is already in storage by this point; without reading it here, a failed
   * sign-in silently drops the very thing the round trip existed to preserve.
   */
  const [storedRedirectPath, setStoredRedirectPath] = useState<string | null>(null);

  const loginHrefWithDestination = buildAuthPathWithRedirect(
    ROUTES.LOGIN,
    storedRedirectPath
  ) as Href;

  useEffect(() => {
    let isMounted = true;

    void loadOAuthRedirectPath().then((path) => {
      if (isMounted) {
        setStoredRedirectPath(path);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const [status, setStatus] = useState<'idle' | 'working' | 'error'>('idle');

  const [errType, setErrType] = useState<OAuthClientError | undefined>(undefined);

  const [isNewUser, setIsNewUser] = useState(false);

  const [userFullName, setUserFullName] = useState<string | null>(null);

  const [userFirstName, setUserFirstName] = useState<string>('');

  const [userLastName, setUserLastName] = useState<string>('');

  const [identifiesAsDoctor, setIdentifiesAsDoctor] = useState<boolean | null>(null);

  const [userEmail, setUserEmail] = useState<string>('');

  const [isLinkingUI, setIsLinkingUI] = useState(false);

  const [hasCustomUserId, setHasCustomUserId] = useState(false);

  const [reconcileOutcome, setReconcileOutcome] = useState<string | undefined>(undefined);

  const hasExchangedRef = useRef(false);

  const [showSupportModal, setShowSupportModal] = useState(false);

  /**
   * `errorType` picks the screen the user sees. `detail.reason` is what analytics records
   * and defaults to the same value, but several different failures share the
   * `token_validation_failed` screen, so a call site that knows better names its own reason
   * and `stage` rather than leaving the alert to guess.
   */
  const handleError = (
    errorType: OAuthClientError,
    detail?: { reason?: string; stage: string }
  ) => {
    setStatus('error');

    setErrType(errorType);

    /*
     * The secure layout holds every render behind `isAuthResolved` until this atom
     * flips, so every branch that stops the OAuth exchange short of a signed-in user
     * has to be the one to flip it — otherwise a later client-side navigation into the
     * secure group renders blank forever instead of bouncing to sign-in.
     */
    setAuthStateResolved(true);

    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.removeItem(SESSION_STORAGE_KEYS.SILENT_REAUTH_ATTEMPTED);
      }
    } catch {}

    trackAuthCallbackError({
      method: 'oauth',
      source: 'callback',
      reason: detail?.reason ?? errorType,
      stage: detail?.stage,
      hasCode: Boolean(code),
    });
  };

  /**
   * Load current user and redirect to appropriate destination
   */
  const loadUserAndRedirect = async (errorContext: string) => {
    try {
      await loadCurrentUser();

      await redirectAfterAuth();
    } catch (error) {
      console.error(`[Auth] Failed to load ${errorContext}:`, error);

      handleError('token_validation_failed', {
        reason: 'user_load_failed',
        stage: 'load_user_and_redirect',
      });
    }
  };

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const meta = document.createElement('meta');

      meta.httpEquiv = 'Cache-Control';

      meta.content = 'no-store';

      document.head.appendChild(meta);
    }
  }, []);

  useEffect(() => {
    const run = async () => {
      if (hasExchangedRef.current) {
        return;
      }

      hasExchangedRef.current = true;

      if (error === 'access_denied') {
        handleError('access_denied');

        return;
      }

      const isSilentReauth = stateParams.get('silent') === '1';

      let storedContext: Awaited<ReturnType<typeof loadAccountLinkingContext>> = null;

      try {
        storedContext = await loadAccountLinkingContext();
      } catch (err) {
        console.warn('[Auth] Failed to load account linking context:', err);
      }

      // Load redirect path from storage (works on both web and native)
      const redirectPath = await loadOAuthRedirectPath();

      setIsLinkingUI(hasLinkIntent);

      if (
        typeof window !== 'undefined' &&
        window.sessionStorage?.getItem('amplify_oauth_completed')
      ) {
        window.sessionStorage.removeItem('amplify_oauth_completed');
      }

      try {
        const session = await fetchAuthSession();

        const idToken = session.tokens?.idToken?.toString();

        if (!idToken) {
          if (code && !error && isWeb && typeof window !== 'undefined') {
            const alreadyRetried =
              window.sessionStorage?.getItem(SESSION_STORAGE_KEYS.SILENT_REAUTH_ATTEMPTED) === '1';

            if (!alreadyRetried) {
              const config = getOAuthConfig();

              if (config) {
                window.sessionStorage?.setItem(SESSION_STORAGE_KEYS.SILENT_REAUTH_ATTEMPTED, '1');

                const url = buildOAuthUrl({
                  ...config,
                  prompt: 'none',
                  state: `silent=1&nonce=${randomUUID()}`,
                });

                window.location.replace(url);

                return;
              }
            }
          }

          console.error('[Auth] No ID token in session', {
            hasCode: !!code,
            hasState: !!state,
            hasError: !!error,
            sessionTokens: session.tokens
              ? {
                  hasAccessToken: !!session.tokens.accessToken,
                  hasIdToken: !!session.tokens.idToken,
                }
              : null,
          });

          Sentry.captureMessage('[Auth] No ID token in session after OAuth callback', {
            level: 'error',
            tags: {
              auth_flow: 'oauth_callback',
              hasCode: !!code,
              hasError: !!error,
            },
            extra: {
              code: code || null,
              state: state || null,
              error: error || null,
              sessionHasTokens: !!session.tokens,
            },
          });

          handleError('token_validation_failed', {
            reason: 'callback_no_session',
            stage: 'no_id_token',
          });

          return;
        }

        const claims = parseIdTokenClaims(idToken);

        if (!claims) {
          console.error('[Auth] Failed to parse ID token');

          handleError('token_validation_failed', {
            reason: 'token_claims_invalid',
            stage: 'parse_id_token',
          });

          return;
        }

        const userHasId = Boolean(claims['custom:user_id']);

        const isOAuthUser = Array.isArray(claims.identities) && claims.identities.length > 0;

        if (!isOAuthUser) {
          setHasCustomUserId(userHasId);
        }

        if (!isOAuthUser) {
          setIsNewUser(!userHasId);
        }

        setStatus('working');

        const provider = isOAuthUser
          ? (Array.isArray(claims.identities) && claims.identities[0]?.providerName
              ? claims.identities[0].providerName
              : ''
            ).toLowerCase()
          : undefined;

        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Auth callback received',
          level: 'info',
          data: { provider, isSilentReauth },
        });

        setUserEmail(claims.email || '');

        let reconcileResult: Awaited<ReturnType<typeof reconcileProvider>> | null = null;

        try {
          reconcileResult = await reconcileProvider();

          setReconcileOutcome(reconcileResult?.outcome);

          if (reconcileResult?.linkStatus) {
            setIsNewUser(false);

            setHasCustomUserId(true);

            try {
              const { tokens } = await fetchAuthSession();

              const idTokenForMe = tokens?.idToken?.toString();

              if (idTokenForMe) {
                const currentUser = await getCurrentUserV2({
                  Authorization: `Bearer ${idTokenForMe}`,
                });

                if (currentUser?.id) {
                  setUserFirstName(currentUser.firstName || '');

                  setUserLastName(currentUser.lastName || '');

                  setUserFullName(
                    [currentUser.firstName || '', currentUser.lastName || '']
                      .filter(Boolean)
                      .join(' ') || null
                  );

                  setUserId(String(currentUser.id));

                  setAuthStateResolved(true);

                  const linkStatus = reconcileResult?.linkStatus;

                  if (linkStatus === 'new') {
                    trackAccountLinked({
                      method: 'auto',
                      provider: 'google',
                      source: 'callback',
                      userId: String(currentUser.id),
                      email: claims.email,
                      linkStatus: 'new',
                    });
                  }

                  void trackLoginSuccess(
                    {
                      userId: String(currentUser.id),
                      email: currentUser.email || claims.email,
                      firstName: currentUser.firstName || undefined,
                      lastName: currentUser.lastName || undefined,
                    },
                    'callback',
                    {
                      method: 'oauth',
                      provider,
                      reconciled: true,
                      linkStatus: linkStatus || undefined,
                      isSilentReauth,
                    }
                  ).catch((err) => {
                    console.warn('[Auth] trackLoginSuccess failed (non-blocking)', err);
                  });

                  try {
                    if (typeof window !== 'undefined' && window.sessionStorage) {
                      window.sessionStorage.removeItem(
                        SESSION_STORAGE_KEYS.SILENT_REAUTH_ATTEMPTED
                      );

                      window.sessionStorage.setItem('primum_oauth_session', '1');
                    }
                  } catch {}

                  await redirectAfterAuth();

                  return;
                }

                if (!isSilentReauth) {
                  Sentry.addBreadcrumb({
                    category: 'auth',
                    message: 'Silent reauth triggered',
                    level: 'info',
                    data: { provider },
                  });

                  triggerSilentReauth();

                  return;
                }
              }
            } catch (err) {
              console.warn('[Auth] /v2/users/me verification failed:', err);

              Sentry.captureException(err);
            }
          }
        } catch (e) {
          console.error('[Auth] reconcile-provider failed:', e);

          Sentry.captureException(e, {
            tags: {
              auth_flow: 'reconcile_provider',
              provider,
              hasUserId: userHasId,
              isOAuthUser,
            },
            extra: {
              error: e instanceof Error ? e.message : String(e),
              reconcileError: JSON.stringify(e, null, 2),
            },
          });
        }

        const hasUserId = !!claims['custom:user_id'];

        if (reconcileResult?.outcome === 'federated_only' && hasUserId && isOAuthUser) {
          setHasCustomUserId(true);

          setIsNewUser(false);
        }

        if (!hasUserId && storedContext?.duplicateUserId && storedContext?.duplicateIdToken) {
          setIsLinkingUI(true);
        }

        if (hasUserId) {
          if (
            storedContext?.duplicateUserId &&
            storedContext?.duplicateIdToken &&
            (hasLinkIntent || storedContext?.authProvider === 'google')
          ) {
            try {
              setIsLinkingUI(true);

              const response = await linkProvider({
                duplicateUserId: storedContext.duplicateUserId,
                duplicateIdToken: storedContext.duplicateIdToken,
              });

              if (response?.success) {
                trackAccountLinked({
                  method: 'manual',
                  provider: 'google',
                  source: 'callback',
                  userId: claims['custom:user_id'] || undefined,
                  email: claims.email,
                  linkStatus: 'new',
                });

                await clearAccountLinkingContext();
              }
            } catch (e) {
              console.warn('[Auth] Manual account linking failed:', e);

              const isApiError = isApiErrorV2(e);

              const errorCode = isApiError
                ? (e.body as { errorCode?: string })?.errorCode
                : undefined;

              if (errorCode === 'ALREADY_LINKED') {
                trackAccountLinked({
                  method: 'manual',
                  provider: 'google',
                  source: 'callback',
                  userId: claims['custom:user_id'] || undefined,
                  email: claims.email,
                  linkStatus: 'linked',
                });
              } else {
                trackAccountLinkingFailed({
                  method: 'manual',
                  provider: 'google',
                  source: 'callback',
                  error: e instanceof Error ? e.message : String(e),
                  userId: claims['custom:user_id'] || undefined,
                  email: claims.email,
                });
              }
            }
          }

          let loadedUser: Awaited<ReturnType<typeof getCurrentUserV2>> | undefined;

          try {
            loadedUser = await getCurrentUserV2({
              Authorization: `Bearer ${idToken}`,
            });

            if (loadedUser?.identifiesAsDoctor) {
              setIdentifiesAsDoctor(true);
            }

            setUserFirstName(loadedUser?.firstName || '');

            setUserLastName(loadedUser?.lastName || '');

            setUserFullName(
              [loadedUser?.firstName || '', loadedUser?.lastName || ''].filter(Boolean).join(' ') ||
                null
            );

            if (isOAuthUser) {
              setHasCustomUserId(true);

              setIsNewUser(false);
            }
          } catch (err) {
            console.warn('[Auth] Failed to fetch user data for identifiesAsDoctor:', err);
          }

          const linkStatus = reconcileResult?.linkStatus;

          if (linkStatus === 'new') {
            trackAccountLinked({
              method: 'auto',
              provider: 'google',
              source: 'callback',
              userId: claims['custom:user_id'] || undefined,
              email: claims.email,
              linkStatus: 'new',
            });
          }

          const reconciled = Boolean(linkStatus);

          void trackLoginSuccess(
            {
              userId: claims['custom:user_id'] as string,
              email: loadedUser?.email || claims.email,
              firstName: loadedUser?.firstName || undefined,
              lastName: loadedUser?.lastName || undefined,
            },
            'callback',
            {
              method: 'oauth',
              provider,
              reconciled,
              linkStatus: linkStatus || undefined,
              isSilentReauth,
            }
          ).catch((err) => {
            console.warn('[Auth] trackLoginSuccess failed (non-blocking)', err);
          });

          try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
              window.sessionStorage.removeItem(SESSION_STORAGE_KEYS.SILENT_REAUTH_ATTEMPTED);

              window.sessionStorage.setItem('primum_oauth_session', '1');
            }
          } catch {}

          await loadUserAndRedirect('user');

          return;
        }

        if (!reconcileResult?.linkStatus && reconcileResult?.outcome === 'npi_unverified') {
          setIsNewUser(true);

          setUserId(claims.sub);

          setAuthStateResolved(true);

          segmentClient.track(ANALYTICS_EVENTS.CREATE_ACCOUNT_STEP_1_COMPLETED, {
            email: claims.email || '',
          });

          try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
              window.sessionStorage.removeItem(SESSION_STORAGE_KEYS.SILENT_REAUTH_ATTEMPTED);

              window.sessionStorage.setItem('primum_oauth_session', '1');
            }
          } catch {}

          const npiVerificationPath = redirectPath
            ? `/(secure)/npi-verification?redirect_path=${encodeURIComponent(redirectPath)}`
            : '/(secure)/npi-verification';

          replace(npiVerificationPath as Href);

          return;
        }

        if (
          !reconcileResult?.linkStatus &&
          reconcileResult?.outcome === 'no_destination_cognito_user'
        ) {
          Sentry.captureMessage('[Auth] Destination Cognito user missing during reconcile', {
            level: 'info',
          });

          segmentClient.track(ANALYTICS_EVENTS.LOGIN_FAILED, {
            method: 'oauth',
            source: 'callback',
            reason: 'no_destination_cognito_user',
          });

          setStatus('error');

          setErrType('token_validation_failed');

          setAuthStateResolved(true);

          return;
        }

        console.error('[Auth] Unexpected state after reconcile:', {
          linkStatus: reconcileResult?.linkStatus,
          hasUserId,
          outcome: reconcileResult?.outcome,
        });

        handleError('token_validation_failed', {
          reason: 'reconcile_failed',
          stage: 'unexpected_reconcile_state',
        });
      } catch (err) {
        console.error('[Auth] Session fetch error:', err);

        handleError('token_validation_failed', { stage: 'callback_exception' });
      } finally {
        if (storedContext && Date.now() - storedContext.createdAt > 15 * 60 * 1000) {
          await clearAccountLinkingContext().catch((err) => {
            Sentry.addBreadcrumb({
              category: 'auth',
              message: 'Failed to clear expired account linking context in OAuth callback',
              level: 'warning',
              data: { error: err instanceof Error ? err.message : String(err) },
            });
          });
        }
      }
    };

    run();
  }, [code, state, error, replace]);

  const getPrimaryText = () => {
    let displayName: string | null = null;

    if (identifiesAsDoctor && userLastName) {
      displayName = `Dr. ${userLastName}`;
    } else if (userFullName) {
      displayName = userFullName;
    } else if (userFirstName) {
      displayName = userFirstName;
    }

    const result = (() => {
      if (isLinkingUI) {
        return 'Linking your account…';
      }

      if (isNewUser) {
        return `Welcome${displayName ? `, ${displayName}` : ''}!`;
      }

      if (hasCustomUserId) {
        return displayName ? `Welcome back, ${displayName}!` : 'Welcome back!';
      }

      return 'Signing in…';
    })();

    return result;
  };

  const getSecondaryText = () => {
    if (isLinkingUI) {
      return 'This may take a moment.';
    }

    if (isNewUser) {
      return 'Setting up your account…';
    }

    return '';
  };

  if (status === 'working') {
    const secondaryText = getSecondaryText();

    return (
      <VStack className="flex-1 items-center justify-center gap-6 p-6">
        <Spinner size="large" />
        <VStack className="items-center gap-2">
          <Text className="text-center font-semibold text-xl">{getPrimaryText()}</Text>
          {secondaryText ? (
            <Text className="text-center text-typography-500">{secondaryText}</Text>
          ) : null}
        </VStack>
      </VStack>
    );
  }

  if (status === 'error') {
    let errorMessage = 'Session expired, try again.';

    if (errType === 'access_denied') {
      errorMessage = 'Access denied. You cancelled the sign-in.';
    } else if (errType === 'token_validation_failed') {
      errorMessage =
        reconcileOutcome === 'no_destination_cognito_user'
          ? 'We found your account, but there is an issue with your sign-in method.'
          : 'Unable to verify your identity. Please try again.';
    } else if (errType === 'invalid_code') {
      errorMessage = 'Invalid or expired authorization code.';
    } else if (errType === 'idp_switching_not_allowed') {
      errorMessage =
        'This email is already linked to a different sign-in provider. Please use your original sign-in method.';
    }

    const showLinkingIssueBox = reconcileOutcome === 'no_destination_cognito_user';

    const showSwitchingBox = errType === 'idp_switching_not_allowed';

    const showInvalidCodeBox = errType === 'invalid_code';

    const showGenericBox = errType === 'token_validation_failed' && !showLinkingIssueBox;

    const shouldShowBox =
      showLinkingIssueBox || showSwitchingBox || showInvalidCodeBox || showGenericBox;

    const errorBoxTitle = (() => {
      if (showLinkingIssueBox) {
        return 'We found your account, but we had trouble linking it.';
      }

      if (showSwitchingBox) {
        return 'This email is already linked to a different sign-in provider.';
      }

      if (showInvalidCodeBox) {
        return 'Your sign-in link expired.';
      }

      return 'We couldn’t complete your sign-in.';
    })();

    const errorBoxSubtitle = (() => {
      if (showLinkingIssueBox) {
        return "Sign in with email and password or contact support and we'll help you finish setup.";
      }

      if (showSwitchingBox) {
        return 'Use your original sign-in method or contact support and we’ll help you switch.';
      }

      return 'Try signing in again or contact support and we’ll help you finish setup.';
    })();

    return (
      <VStack className="flex-1 items-center justify-center gap-6 p-6">
        {!shouldShowBox ? (
          <Text className="text-center font-semibold text-xl">{errorMessage}</Text>
        ) : null}

        {shouldShowBox ? (
          <VStack className="w-full max-w-[420px] rounded-md bg-background-warning px-4 py-4">
            <HStack className="items-start gap-2">
              <Icon as={AlertTriangleIcon} className="mt-0.5 h-[18px] w-[18px] text-warning-800" />

              <VStack className="flex-1 gap-1.5">
                <Text size="sm" className="font-semibold text-typography-900">
                  {errorBoxTitle}
                </Text>

                <Text size="xs" className="text-typography-700">
                  {errorBoxSubtitle}
                </Text>

                <HStack className="mt-2 items-center">
                  <Link onPress={() => replace(loginHrefWithDestination)}>
                    <LinkText size="xs" className="text-warning-800 underline">
                      Sign in
                    </LinkText>
                  </Link>
                  <Text size="xs" className="mx-1 text-typography-700">
                    or
                  </Text>
                  <Link onPress={() => setShowSupportModal(true)}>
                    <LinkText size="xs" className="text-warning-800 underline">
                      Contact Support
                    </LinkText>
                  </Link>
                </HStack>
              </VStack>
            </HStack>
          </VStack>
        ) : (
          <Button onPress={() => replace(loginHrefWithDestination)}>
            <ButtonText>Back to sign in</ButtonText>
          </Button>
        )}

        {showSupportModal ? (
          <SupportForm
            isOpen={showSupportModal}
            onClose={() => setShowSupportModal(false)}
            initialValues={{ email: userEmail }}
          />
        ) : null}
      </VStack>
    );
  }

  return null;
}
